// helpers.js — Pure helper functions with no dependencies on script.js.
// All deps come from already-extracted modules (config, state, workload-defs, utils).
// Extracted from script.js — 142 functions, ~1,600 lines.

import { effortConfig, blockHours, calcJobHcUnits } from './config.js';
import { architectureState, upgradeScope, sites, hypervisorMigrationScopes,
         workloadPickerUiState, siteScopePickerUiState,
         existingEnvironmentFieldSchema, existingEnvironmentFieldSchemaByKey,
         existingEnvironmentCountFieldOrder,
         existingEnvironmentFieldLabels, defaultStep2CollapseState, AppState } from './state.js';
import { unstructuredDataWorkloadId, repoMigrationWorkloadId, kastenK10WorkloadId,
         veeamBackupForMicrosoft365WorkloadId, inDevelopmentWorkloadValues,
         engagementTypeLabels, implementationModeLabels, architectureOptionLabels,
         getJobTypeDef, getJobWorkloadDef, jobTypeOptions, jobWorkloadOptions,
         replicationAllowedWorkloads, cdpAllowedWorkloads,
         sureBackupAllowedWorkloads, backupCopyExcludedWorkloads,
         hypervisorLabels, cloudLabels, saasLabels, containerLabels, pluginLabels,
         unstructuredDataLabels, sourceWorkloadLabels, agentLabels,
         workloadOptionGroups, vb365RepoTypeOptions } from './workload-defs.js';
import { escapeHtml, isUnstructuredDataWorkload, isRepoMigrationWorkload,
         isProtectedTbWorkload, isVb365ManagedWorkload, isVbfCloudManagedWorkload,
         getVbfCloudProvider, normalizeWorkloadValue,
         calculateUnstructuredDataInstances, normalizeProtectedTb,
         normalizeEnvironmentCount, normalizeToggleMigratedCount,
         formatProtectedTbValue, formatProtectedTbWholeNumber } from './utils.js';


export function updateSavePdfVisibility(step) {
    if (!savePdfRow) return;
    savePdfRow.style.display = (step === totalSteps) ? '' : 'none';
}

export function getAppBuildInfo() {
    const rawBuildInfo = window.APP_BUILD_INFO || {};
    const version = typeof rawBuildInfo.version === 'string' && rawBuildInfo.version.trim()
        ? rawBuildInfo.version.trim()
        : '0.0.0';
    const commit = typeof rawBuildInfo.commit === 'string' && rawBuildInfo.commit.trim()
        ? rawBuildInfo.commit.trim().toUpperCase()
        : 'UNKNOWN';

    return { version, commit };
}

export function getExistingEnvironmentFieldDefinition(field) {
    return existingEnvironmentFieldSchemaByKey.get(field) || null;
}

export function getExistingEnvironmentPrimaryParentField(definition) {
    if (!definition) {
        return '';
    }

    return definition.parentKey || definition.dependsOn || '';
}

export function isExistingEnvironmentChildOf(definition, field) {
    if (!definition || !field) {
        return false;
    }

    return definition.parentKey === field || definition.dependsOn === field;
}

export function resetUpgradeScope() {
    upgradeScope.perVbr = {};
    Object.assign(upgradeScope, { advancedRbac: 'no', vbem: 'yes' });
}

export function createJobId() {
    return AppState.nextJobId++;
}

export function createUnprotectedWorkloadId() {
    return AppState.nextUnprotectedWorkloadId++;
}

export function createHypervisorMigrationId() {
    return AppState.nextHypervisorMigrationId++;
}

export function createVb365RepoMigrationId() {
    return AppState.nextVb365RepoMigrationId++;
}

export function createVbrRepoMigrationId() {
    return AppState.nextVbrRepoMigrationId++;
}

export function createSourceWorkloadId() {
    return AppState.nextSourceWorkloadId++;
}

export function createRepositoryId() {
    return AppState.nextRepositoryId++;
}

// ── Source workload factory functions ─────────────────────────────────────────
//
// A source workload represents a workload TYPE on a site as it exists TODAY
// (Step 1 — current state). It is NOT a backup job or a PS deliverable.
//
// Shape:
//   totalCount — the actual number of VMs/users/TB of this workload type on the site.
//                This is the canonical "how many exist" value used in the SOW and
//                effort calculations. Never additive across jobs.
//   jobs[]     — zero or more Veeam jobs currently protecting some or all of the
//                totalCount. Each job has a type, a count (<= totalCount), and an
//                optional managed-by server link.
//                Zero jobs = fully unprotected.
//
// Example: 500 VMware VMs, all backed up, 200 also replicated:
//   totalCount: 500
//   jobs: [
//     { type: 'backup',      count: 500, managedByServerId: 'ee-vbr-0' },
//     { type: 'replication', count: 200, managedByServerId: 'ee-vbr-0' },
//   ]
//
// Job type values: 'backup' | 'replication' | 'cdp' | 'backupCopy'
// Sub-options:     sureBackup (backup only) · sureReplica (replication only)
//
// The managedByServerId drives Step 2 autoAddLinkedVbrLanes().

/**
 * Builds a single job entry within a source workload.
 * Valid types: 'backup' | 'replication' | 'cdp' | 'backupCopy' | 'sureBackup' | 'sureReplica'
 * SureBackup requires a 'backup' job to exist on the same workload.
 * SureReplica requires a 'replication' job to exist on the same workload.
 * @param {string} type   — job type
 * @param {number} count  — number of VMs/units covered by this job
 * @param {object} [overrides]
 */
export function buildDefaultSourceWorkloadJob(type = 'backup', count = 0, overrides = {}) {
    return {
        type,
        count,
        managedByServerId: null,
        ...overrides,
    };
}

/**
 * Builds a new source workload entry for a site.
 * Starts with zero total count and no jobs (fully unprotected).
 * @param {string} workloadValue — workload type ID (e.g. 'vmware-vsphere')
 */
export function buildDefaultSourceWorkloadEntry(workloadValue = '') {
    return {
        id:            createSourceWorkloadId(),
        workloadValue,
        label:         '',     // optional custom label — shown on chip face if set
        totalCount:    1,
        jobs:          [],
    };
}

/**
 * Builds a new repository entry for a site (Step 1 — current state).
 * In Step 2, repo migration scopes reference repository entries by id.
 * @param {object} [overrides] — optional field overrides
 */
export function buildDefaultRepositoryEntry(overrides = {}) {
    return {
        id:                createRepositoryId(),
        label:             '',      // optional user-supplied name
        type:              '',      // 'standard' | 'hardened' | 'sobr' | 'object-storage'
        consumedTb:        0,
        managedByServerId: null,    // veeamServer.id on this site
        ...overrides,
    };
}

export function getWorkloadTileDisplayLabel(label, unit) {
    if (!label || !unit) return label || '';
    const escapedUnit = unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const stripped = label.replace(new RegExp('\\s+' + escapedUnit + '\\*?\\s*$', 'i'), '').trim();
    return stripped || label;
}

export function normalizeWorkloadIdentifier(identifier) {
    if (Number.isInteger(identifier) && identifier > 0) {
        return identifier;
    }

    const parsedIdentifier = parseInt(identifier, 10);
    if (Number.isInteger(parsedIdentifier) && parsedIdentifier > 0 && String(parsedIdentifier) === String(identifier)) {
        return parsedIdentifier;
    }

    return typeof identifier === 'string' ? identifier : '';
}

export function buildWorkloadPickerKey(siteId, workloadRowId) {
    return `${siteId}:${workloadRowId}`;
}

export function hasSelectedWorkloadValue(value) {
    return typeof value === 'string'
        && Boolean(value)
        && (
            Object.hasOwn(sourceWorkloadLabels, value)
            || Object.hasOwn(agentLabels, value)
            || isRepoMigrationWorkload(value)
        );
}

export function isInDevelopmentWorkloadValue(value = '') {
    return typeof value === 'string' && inDevelopmentWorkloadValues.has(value);
}

export function buildWorkloadPickerGroupId(prefix, groupLabel) {
    const token = groupLabel
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'group';

    return `${prefix}-${token}`;
}

export function getSiteWorkloadPickerTriggerSelector(siteId, workloadRowId) {
    return `[data-site-id="${siteId}"] [data-site-workload-picker-trigger="${workloadRowId}"]`;
}

export function getSiteWorkloadPickerSearchSelector(siteId, workloadRowId) {
    return `[data-site-id="${siteId}"] [data-site-workload-picker-search="${workloadRowId}"]`;
}

export function normalizeWorkloadPickerSearchQuery(searchQuery) {
    return String(searchQuery || '').trim().toLowerCase();
}

export function getWorkloadPickerGroupLabel(workloadValue = '') {
    for (const group of workloadOptionGroups) {
        if (group.options.some((option) => option.value === workloadValue)) {
            return group.label;
        }
    }

    return '';
}

export function parseWorkloadPickerKey(key) {
    const normalizedKey = String(key || '');
    const separatorIndex = normalizedKey.indexOf(':');
    if (separatorIndex === -1) {
        return null;
    }

    const siteId = parseInt(normalizedKey.slice(0, separatorIndex), 10);
    const workloadRowId = parseInt(normalizedKey.slice(separatorIndex + 1), 10);
    if (!siteId || !workloadRowId) {
        return null;
    }

    return { siteId, workloadRowId };
}

export function clearWorkloadPickerPortal() {
    if (workloadPickerRepositionFrame) {
        window.cancelAnimationFrame(workloadPickerRepositionFrame);
        workloadPickerRepositionFrame = 0;
    }

    workloadPickerPortalHost.hidden = true;
    workloadPickerPortalHost.innerHTML = '';
}

export function getWorkloadPickerViewportBottomBoundary(viewportHeight, viewportPadding) {
    const wizardFooter = document.querySelector('.wizard-footer');
    let bottomBoundary = viewportHeight - viewportPadding;

    if (!(wizardFooter instanceof HTMLElement)) {
        return bottomBoundary;
    }

    const footerRect = wizardFooter.getBoundingClientRect();
    const isFooterVisible = footerRect.width > 0
        && footerRect.height > 0
        && footerRect.top < viewportHeight
        && footerRect.bottom > 0;

    if (!isFooterVisible) {
        return bottomBoundary;
    }

    return Math.max(viewportPadding, Math.min(bottomBoundary, Math.round(footerRect.top - 12)));
}

export function isActiveElementWithinWorkloadPicker(pickerKey) {
    if (!(document.activeElement instanceof Node) || !pickerKey) {
        return false;
    }

    const refreshedRoot = siteList.querySelector(`[data-workload-picker-key="${pickerKey}"]`);
    if (refreshedRoot instanceof HTMLElement && refreshedRoot.contains(document.activeElement)) {
        return true;
    }

    const portalPanel = workloadPickerPortalHost.querySelector(`[data-workload-picker-key="${pickerKey}"]`);
    return portalPanel instanceof HTMLElement && portalPanel.contains(document.activeElement);
}

export function buildSiteScopePickerKey(pickerType, siteId, itemId) {
    return `${pickerType}:${siteId}:${String(itemId || '')}`;
}

export function parseSiteScopePickerKey(key) {
    const normalizedKey = String(key || '');
    const [pickerType, siteIdRaw, ...itemParts] = normalizedKey.split(':');
    const siteId = parseInt(siteIdRaw, 10);
    const itemId = itemParts.join(':');

    if (!pickerType || !siteId || !itemId) {
        return null;
    }

    return { pickerType, siteId, itemId };
}

export function getSiteScopePickerTriggerSelector(key) {
    return `[data-site-scope-picker-trigger="${key}"]`;
}

export function getSiteScopePickerSearchSelector(key) {
    return `[data-site-scope-picker-search="${key}"]`;
}

export function getSiteScopePickerSearchQuery(key) {
    return siteScopePickerUiState.searchQueryByKey[key] || '';
}

export function setSiteScopePickerSearchQuery(key, searchQuery) {
    siteScopePickerUiState.searchQueryByKey[key] = String(searchQuery || '');
}

export function isSiteScopePickerOpen(key) {
    return siteScopePickerUiState.openKey === key;
}

export function getRepoMigrationPickerOptionLabel(options = [], value = '') {
    return options.find((option) => option.value === value)?.label || '';
}

export function clearSiteScopePickerPortal() {
    if (siteScopePickerRepositionFrame) {
        window.cancelAnimationFrame(siteScopePickerRepositionFrame);
        siteScopePickerRepositionFrame = 0;
    }

    siteScopePickerPortalHost.hidden = true;
    siteScopePickerPortalHost.innerHTML = '';
}

export function isActiveElementWithinSiteScopePicker(pickerKey) {
    if (!(document.activeElement instanceof Node) || !pickerKey) {
        return false;
    }

    const refreshedRoot = siteList.querySelector(`[data-site-scope-picker-key="${pickerKey}"]`);
    if (refreshedRoot instanceof HTMLElement && refreshedRoot.contains(document.activeElement)) {
        return true;
    }

    const portalPanel = siteScopePickerPortalHost.querySelector(`[data-site-scope-picker-key="${pickerKey}"]`);
    return portalPanel instanceof HTMLElement && portalPanel.contains(document.activeElement);
}

export function createWorkloadRowId(initialValue = null) {
    const normalizedValue = Number.isInteger(initialValue) && initialValue > 0 ? initialValue : AppState.nextWorkloadRowId;
    AppState.nextWorkloadRowId = Math.max(AppState.nextWorkloadRowId, normalizedValue + 1);
    return normalizedValue;
}

export function isExistingEnvironmentFieldAvailableForEngagement(definition) {
    if (!definition) {
        return false;
    }

    // Unified canvas: isExistingEnvironmentInScope() always false; only show fields without engagementTypes filter.
    return !definition.engagementTypes;
}

export function normalizeSiteName(value) {
    return typeof value === 'string' ? value.trim() : '';
}

export function getDefaultSiteName(index) {
    if (index <= 0) {
        return 'Primary Site';
    }

    if (index === 1) {
        return 'Secondary Site';
    }

    return `Tertiary Site (${index - 1})`;
}

export function getExistingEnvironmentCountFieldOrder() {
    // Unified canvas: isExistingEnvironmentInScope() always false; always returns base field order.
    return existingEnvironmentCountFieldOrder;
}

export function normalizeWorkloadCustomLabel(value) {
    return typeof value === 'string' ? value.trim() : '';
}

export function getUnavailableSiteWorkloadValues(site, selectedValue = '') {
    return new Set(
        Array.isArray(site?.workloads)
            ? site.workloads
                .map((workload) => workload?.value)
                .filter((value) => value && value !== selectedValue)
            : []
    );
}

export function sanitizeRepoMigrationRowState(rawState = {}) {
    return {
        vdcVaultSourceStorageType: typeof rawState.vdcVaultSourceStorageType === 'string'
            ? rawState.vdcVaultSourceStorageType
            : (typeof rawState.sourceStorageType === 'string' ? rawState.sourceStorageType : ''),
        vdcVaultRepositoryType: typeof rawState.vdcVaultRepositoryType === 'string'
            ? rawState.vdcVaultRepositoryType
            : (typeof rawState.repositoryType === 'string' ? rawState.repositoryType : ''),
        vdcVaultTargetVault: typeof rawState.vdcVaultTargetVault === 'string'
            ? rawState.vdcVaultTargetVault
            : (typeof rawState.targetVault === 'string' ? rawState.targetVault : ''),
        // Preserve the flag that marks this entry as a component-drawer repository
        // (not a standalone vault migration) — prevents engagement-type from flipping to vdc-vault
        ...(rawState.isRepositoryComponent ? { isRepositoryComponent: true } : {})
    };
}

export function getProtectedTbSummary(protectedTb) {
    return `${formatProtectedTbValue(protectedTb)} TB protected`;
}

export function getRoundedProtectedTbSummary(protectedTb) {
    return `${formatProtectedTbWholeNumber(protectedTb)} TB protected`;
}

export function hasRepositoryComponents() {
    return sites.some((s) =>
        Array.isArray(s.workloads) &&
        s.workloads.some((w) => w.isRepositoryComponent && isRepoMigrationWorkload(w.value))
    );
}

export function getRepoMigrationWorkloads(site) {
    return Array.isArray(site?.workloads)
    ? site.workloads.filter((workload) => isRepoMigrationWorkload(workload.value))
        : [];
}

export function getRepoMigrationWorkloadProtectedTb(workload) {
    return normalizeProtectedTb(workload?.protectedTb ?? workload?.tb ?? 0);
}

/**
 * Maps a VBR repository workload (from Step 1) to the sourceType value used
 * by vbrRepoMigrationScopes in Step 2.
 *
 * Mapping:
 *   SOBR Performance Tier or Capacity Tier → 'sobr-performance-tier'
 *   Stand-alone on block / dedup storage   → 'block'
 *   Stand-alone on any other storage       → 'standalone'
 *
 * Shared by vbr-repo-migration.js (dropdown options) and recalc.js (effort calc).
 */
export function mapVbrRepoWorkloadToSourceType(w) {
    const repoType    = w?.vdcVaultRepositoryType    || '';
    const sourceStore = w?.vdcVaultSourceStorageType || '';
    if (repoType === 'sobr-performance-tier' || repoType === 'sobr-capacity-tier') {
        return 'sobr-performance-tier';
    }
    if (sourceStore === 'onprem-block' || sourceStore === 'dedup-appliance') {
        return 'block';
    }
    return 'standalone';
}

export function describeRepoMigrationSelectionGaps(site) {
    const missingLabels = [];

    if (site.missingSourceStorage) {
        missingLabels.push('source storage');
    }

    if (site.missingRepository) {
        missingLabels.push('repository');
    }

    if (missingLabels.length === 1) {
        return `a ${missingLabels[0]}`;
    }

    if (missingLabels.length === 2) {
        return `a ${missingLabels[0]} and ${missingLabels[1]}`;
    }

    return `a ${missingLabels.slice(0, -1).join(', ')}, and ${missingLabels[missingLabels.length - 1]}`;
}

export function updateDerivedInstancePreview(siteId, workloadIdentifier, instanceCount) {
    const derivedCount = siteList.querySelector(
        `[data-site-workload-derived-count="${workloadIdentifier}"][data-site-id="${siteId}"]`
    );

    if (derivedCount) {
        derivedCount.textContent = `Calculated instances: ${instanceCount}`;
    }
}

export function getWorkloadType(value) {
    if (isRepoMigrationWorkload(value)) {
        return 'Migration';
    }

    if (Object.hasOwn(hypervisorLabels, value)) {
        return 'Platform';
    }

    if (Object.hasOwn(cloudLabels, value)) {
        return 'Cloud';
    }

    if (Object.hasOwn(saasLabels, value)) {
        return 'SaaS';
    }

    if (Object.hasOwn(containerLabels, value)) {
        return 'Containers';
    }

    if (Object.hasOwn(pluginLabels, value)) {
        return 'Plug-In';
    }

    if (Object.hasOwn(unstructuredDataLabels, value)) {
        return 'Unstructured Data';
    }

    if (Object.hasOwn(agentLabels, value)) {
        return 'Agent';
    }

    return 'Workload';
}

export function getExistingEnvironmentIconMarkup(key) {
    return {
        vbrServers: vbrIconMarkup,
        vbemServers: vbemIconMarkup,
        veeamOneServers: veeamOneIconMarkup,
        vroServers: vroIconMarkup,
        proxyServersWindows: backupProxyIconMarkup,
        proxyServersLinux: backupProxyIconMarkup,
        repositoryServers: backupRepositoryIconMarkup,
        wanAccelerators: wanAcceleratorIconMarkup,
        veeamAgents: veeamAgentIconMarkup,
        enterprisePlugins: enterprisePluginIconMarkup,
        backupJobs: backupJobsIconMarkup,
        sureBackupJobs: sureBackupJobsIconMarkup,
        cdpPolicies: cdpPoliciesIconMarkup,
        replicationJobs: replicationJobsIconMarkup,
        virtualLab: virtualLabIconMarkup,
        vb365ProxyServers: vb365ProxyIconMarkup,
        vb365RepositoryServers: vb365RepositoryIconMarkup,
        vbfCloudRepositoryServers: vbfCloudRepositoryIconMarkup
    }[key] || '';
}

export function getSiteNamePlaceholder() {
    return siteNamePlaceholder;
}

export function renderEmptyOutput() {
    output.innerHTML = emptyOutputMarkup;
}

export function renderOutputMessage(message) {
    output.innerHTML = `<div class="output-card"><p class="results-empty">${escapeHtml(message)}</p></div>`;
}

export function clearStepValidationMessage() {
    if (stepValidationMessage) {
        stepValidationMessage.hidden = true;
        stepValidationMessage.innerHTML = '';
        stepValidationMessage.classList.remove('step-validation-message--warning');
    }

    const nextStepHint = document.getElementById('next-step-hint');
    if (nextStepHint) {
        clearTimeout(nextStepHint._flashTimer);
        nextStepHint.classList.remove('wizard-nav__hint--flash-error', 'wizard-nav__hint--flash-warning');
        delete nextStepHint.dataset.hintPinned;
        // Restore default text
        const textEl = nextStepHint.querySelector('.wizard-nav__hint-text');
        if (textEl) textEl.textContent = '';
    }
}

export function showStepValidationMessage(message, tone = 'error') {

    // Suppress the old red box entirely.
    if (stepValidationMessage) {
        stepValidationMessage.hidden = true;
    }

    const nextStepHint = document.getElementById('next-step-hint');
    if (!nextStepHint) return;

    // Update pill text to the first (most important) message.
    const messages = Array.isArray(message) ? message : [message];
    const textEl = nextStepHint.querySelector('.wizard-nav__hint-text');
    if (textEl) textEl.textContent = ' ' + messages[0];

    // Pin the hint visible regardless of button state, then flash it.
    nextStepHint.dataset.hintPinned = 'true';
    nextStepHint.hidden = false;
    nextStepHint.scrollIntoView({ block: 'nearest' });

    // Flash: add class immediately, remove after a short hold so the transition fades it back out.
    const flashClass = tone === 'warning' ? 'wizard-nav__hint--flash-warning' : 'wizard-nav__hint--flash-error';
    clearTimeout(nextStepHint._flashTimer);
    nextStepHint.classList.remove('wizard-nav__hint--flash-error', 'wizard-nav__hint--flash-warning');
    void nextStepHint.offsetWidth; // force reflow so transition fires from base state
    nextStepHint.classList.add(flashClass);
    nextStepHint._flashTimer = setTimeout(() => {
        nextStepHint.classList.remove(flashClass);
    }, 1000);
}

export function getPersistentStorage() {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

export function getRestorableCurrentStep(value) {
    const parsedValue = parseInt(value, 10);
    if (Number.isNaN(parsedValue)) {
        return 1;
    }

    return Math.min(Math.max(parsedValue, 1), totalSteps);
}

export function normalizeLocalScenarioName(value) {
    return typeof value === 'string' ? value.trim() : '';
}

export function setLocalScenarioStatus(message) {
    if (localScenarioStatus) {
        localScenarioStatus.textContent = message;
    }
}

export function updateThemeToggleLabel(theme) {
    if (!themeToggle) {
        return;
    }

    const isDark = theme === 'dark';
    const icon = isDark ? '☀' : '☾';
    themeToggle.dataset.icon = icon;
    themeToggle.setAttribute('aria-pressed', String(isDark));
}

export function toggleValue(toggle) {
    return toggle.checked ? 'yes' : 'no';
}

export function hasExplicitVeeamOneChoice() {
    return veeamOneRequiredToggle?.dataset.explicitChoice === 'true';
}

export function toggleLabel(value) {
    return value === 'yes' ? 'Yes' : 'No';
}

export function getCanvasEngagementFlags() {
    const allWorkloads   = sites.flatMap((s) => s.workloads || []).filter((w) => w.value);
    // Exclude entries flagged as repository components — those live in the component group and
    // must not push the engagement type into the standalone vault-migration mode.
    const hasVault       = allWorkloads.some((w) => isRepoMigrationWorkload(w.value) && !w.isRepositoryComponent);
    const hasWorkloads   = allWorkloads.some((w) => !isRepoMigrationWorkload(w.value));
    // Source workloads (Phase 1+ refactor) — replaces unprotectedWorkloads check.
    // unprotectedWorkloads was removed in Phase 6; checking it always returns false now.
    const hasSourceWorkloads = sites.some((s) => Array.isArray(s.sourceWorkloads) && s.sourceWorkloads.length > 0);
    const hasProtected   = sites.some((s) => (Array.isArray(s.jobs) && s.jobs.length > 0) || (Array.isArray(s.protectedWorkloads) && s.protectedWorkloads.some((w) => w.value)));
    const hasComponents  = sites.some((s) => {
        // Check both legacy existingEnvironment AND new veeamServers array
        const env = s.existingEnvironment || {};
        const hasLegacyEnv = Object.values(env).some((v) => normalizeEnvironmentCount(v) > 0);
        const hasServers   = Array.isArray(s.veeamServers) && s.veeamServers.length > 0;
        return hasLegacyEnv || hasServers;
    });
    const hasVbr = sites.some((s) => Array.isArray(s.veeamServers) && s.veeamServers.some((sv) => sv.type === 'vbr'));
    return { hW: hasWorkloads || hasSourceWorkloads, hV: hasVault, hC: hasComponents, hVBR: hasVbr, hP: hasProtected, hU: hasSourceWorkloads };
}

/**
 * Derives the engagement type string from the current canvas state.
 * Single source of truth — replaces the inline ternary in recalc.js and script.js.
 *   'vdc-vault'  — vault workloads only, no source workloads or components
 *   'upgrade'    — components only, no source workloads or vault
 *   'greenfield' — everything else (source workloads, mixed, or empty)
 */
export function deriveEngagementType() {
    const { hV, hW, hC } = getCanvasEngagementFlags();
    if (hV && !hW && !hC) return 'vdc-vault';
    if (hC && !hW && !hV) {
        // Pure existing-environment canvas.
        // 'health-check': Step 2 matrix has HC selections but no upgrade selections.
        // 'upgrade':       any upgrade selection is present (HC may also be present —
        //                  HC + upgrade is valid and reduces pre-flight effort).
        const hcMatrixActive = !!(document.getElementById('upgrade-health-check-review-required')?.checked);
        const matrixComponents = window.step2MatrixState?.components || {};
        const anyTypeUpgrade = Object.values(matrixComponents).some(
            (c) => c?.upgrade?.linuxVsa === true
        );
        // Per-VBR upgrade state lives in upgradeScope.perVbr (imported from state.js).
        const anyVbrUpgrade = Object.values(upgradeScope.perVbr || {}).some(
            (s) => s?.linuxVsa === true
        );
        const anyUpgrade = anyTypeUpgrade || anyVbrUpgrade;
        if (hcMatrixActive && !anyUpgrade) return 'health-check';
        return 'upgrade';
    }
    return 'greenfield';
}

export function shouldShowArchitectureSiteTopology(siteCount = sites.length) {
    return siteCount > 1;
}

export function inferSiteTopology(siteCount = sites.length) {
    if (siteCount <= 1) return '';
    // Step 2 canvas: if VBR lanes are spread across more than one site → distributed
    const sitesInCanvas = new Set(step2VbrLanes.map((l) => l.siteId));
    if (sitesInCanvas.size > 1) return 'distributed';
    // Step 1 fallback: if more than one site already has an existing VBR server → distributed
    const sitesWithVbr = sites.filter((s) =>
        normalizeToggleMigratedCount((s.existingEnvironment || {}).vbrServers) > 0 ||
        (s.veeamServers || []).some((sv) => sv.type === 'vbr')
    );
    if (sitesWithVbr.length > 1) return 'distributed';
    return 'centralized';
}

export function resolvePlatformArchitectureState(baseArchitecture, isAdvanced) {
    if (isAdvanced) {
        const resolvedRemoteDb = baseArchitecture.remoteDb === 'yes' ? 'yes' : 'no';
        const resolvedDatabaseType = resolvedRemoteDb === 'yes'
            ? (Object.hasOwn(architectureOptionLabels.databaseType, baseArchitecture.databaseType) ? baseArchitecture.databaseType : 'postgresql')
            : 'postgresql';

        return {
            ...baseArchitecture,
            databaseType: resolvedDatabaseType,
            remoteDb: resolvedRemoteDb,
            sqlAoag: resolvedRemoteDb === 'yes' && resolvedDatabaseType === 'mssql' && baseArchitecture.sqlAoag === 'yes'
                ? 'yes'
                : 'no'
        };
    }

    return {
        ...baseArchitecture,
        osPlatform: 'linux',
        remoteConfigurationDatabase: 'yes',
        vbem: 'yes',
        vbrHa: 'no',
        databaseType: 'postgresql',
        remoteDb: 'no',
        sqlAoag: 'no',
        advancedRbac: 'no'
    };
}

export function setSectionVisibility(section, isVisible) {
    if (!section) {
        return;
    }

    if (!isVisible && section.contains(document.activeElement)) {
        document.activeElement.blur();
    }

    section.hidden = !isVisible;
    section.style.display = isVisible ? '' : 'none';
}

export function resetStep2CollapseState() {
    Object.keys(AppState.step2CollapseState).forEach((k) => delete AppState.step2CollapseState[k]);
    Object.assign(AppState.step2CollapseState, defaultStep2CollapseState);
}

export function setCollapsibleSectionState(sectionId, isCollapsed) {
    const section = document.querySelector(`[data-collapsible-section="${sectionId}"]`);
    if (!section) {
        return;
    }

    const triggers = Array.from(section.querySelectorAll('[data-collapsible-trigger]'));
    const content = section.querySelector('[data-collapsible-content]');
    if (!triggers.length || !content) {
        return;
    }

    const collapsed = Boolean(isCollapsed);
    AppState.step2CollapseState[sectionId] = collapsed;
    section.dataset.collapsed = collapsed ? 'true' : 'false';
    triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(!collapsed));
    });
    if (collapsed && section.contains(document.activeElement)) {
        document.activeElement.blur();
    }
    content.hidden = collapsed;
}

export function syncMigrationGroupVisibility() {
    if (!designMigrationGroup) return;
    const hvmVisible            = hypervisorMigrationSection     && !hypervisorMigrationSection.hidden;
    const vsaVisible            = vsaMigrationSection            && !vsaMigrationSection.hidden;
    const vb365RepoVisible      = window.vb365RepoMigrationActive === true;
    const vbrRepoVisible        = window.vbrRepoMigrationActive   === true;

    // Sync the inner Repository Migration group wrapper
    const repoMigrationGroup = document.getElementById('design-repo-migration-group');
    if (repoMigrationGroup) {
        const showRepoGroup = vbrRepoVisible || vb365RepoVisible;
        repoMigrationGroup.hidden = !showRepoGroup;
        repoMigrationGroup.style.display = showRepoGroup ? '' : 'none';
    }

    const show = hvmVisible || vsaVisible || vb365RepoVisible || vbrRepoVisible;
    designMigrationGroup.hidden = !show;
    designMigrationGroup.style.display = show ? '' : 'none';
    designMigrationGroup.setAttribute('aria-hidden', String(!show));
}

export function bindGroupToggleEvents(root = document) {
    root.querySelectorAll('[data-group-trigger]').forEach((trigger) => {
        if (trigger.dataset.groupToggleBound === 'true') return;
        trigger.addEventListener('click', () => {
            const targetId = trigger.getAttribute('aria-controls');
            if (!targetId) return;
            const body = document.getElementById(targetId);
            const group = trigger.closest('.design-section-group');
            if (!body || !group) return;
            const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
            const newExpanded = !isExpanded;
            body.hidden = !newExpanded;
            group.dataset.collapsed = String(!newExpanded);
            group.querySelectorAll('[data-group-trigger]').forEach((t) => {
                t.setAttribute('aria-expanded', String(newExpanded));
            });
        });
        trigger.dataset.groupToggleBound = 'true';
    });
}

export function renderArchitectureChoiceGroup(field, selectedValue, options, ariaLabel) {
    return `
        <div class="segmented-control" role="radiogroup" aria-label="${escapeHtml(ariaLabel)}">
            ${options.map((option) => `
                <label class="segmented-control__option" for="architecture-${field}-${option.value}">
                    <input
                        type="radio"
                        id="architecture-${field}-${option.value}"
                        name="architecture-${field}"
                        value="${escapeHtml(option.value)}"
                        data-architecture-field="${escapeHtml(field)}"
                        ${option.value === selectedValue ? 'checked' : ''}
                    >
                    <span class="segmented-control__pill">${escapeHtml(option.label)}</span>
                </label>
            `).join('')}
        </div>
    `;
}

export function syncUpgradeScopeToArchitectureState() {
    architectureState.advancedRbac = upgradeScope.advancedRbac;
    architectureState.vbem         = upgradeScope.vbem;
}


export function syncSureReplicaControl() {
    if (!sureReplicaRequiredInput || sureReplicaDisplayInputs.length === 0) {
        return;
    }

    const normalizedValue = sureReplicaRequiredInput.value === 'yes' ? 'yes' : 'no';
    sureReplicaRequiredInput.value = normalizedValue;

    sureReplicaDisplayInputs.forEach((input) => {
        input.checked = input.value === normalizedValue;
    });
}

export function syncProtectionTileVisuals() {
    function setTileActive(selector, isActive) {
        const el = document.querySelector(selector);
        if (!el) return;
        el.classList.toggle('prot-tile--active', !!isActive);
        el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
    function setSubChipActive(selector, isActive) {
        const el = document.querySelector(selector);
        if (!el) return;
        el.classList.toggle('prot-sub-chip--active', !!isActive);
        el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }

    const backupActive = backupVerificationRequiredInput?.value !== 'no';
    const backupCopyActive = document.getElementById('backup-copy-required')?.checked ?? false;
    const replicationActive = replicationRequiredToggle?.checked ?? false;
    const cdpActive = document.getElementById('cdp-replication-required')?.checked ?? false;
    const tapeActive = tapeIntegrationRequiredToggle?.checked ?? false;
    const siemActive = document.getElementById('siem-forwarding-required')?.checked ?? false;
    const sureBackupActive = sureBackupRequiredInput?.value === 'yes';
    const sureReplicaActive = sureReplicaRequiredInput?.value === 'yes';

    setTileActive('[data-prot-tile="backup"]', backupActive);
    setTileActive('[data-prot-tile="backup-copy"]', backupCopyActive);
    setTileActive('[data-prot-tile="replication"]', replicationActive);
    setTileActive('[data-prot-tile="tape"]', tapeActive);
    setTileActive('[data-prot-tile="siem"]', siemActive);
    setSubChipActive('[data-prot-sub-chip="sure-backup"]', sureBackupActive);
    setSubChipActive('[data-prot-sub-chip="cdp"]', cdpActive);
    setSubChipActive('[data-prot-sub-chip="sure-replica"]', sureReplicaActive);
}

export function getImplementationMode() {
    return document.querySelector('input[name="implementation-mode"]:checked')?.value || 'none';
}

export function sanitizeImplementationMode(rawState) {
    if (typeof rawState.implementationMode === 'string' && Object.hasOwn(implementationModeLabels, rawState.implementationMode)) {
        return rawState.implementationMode;
    }

    // Legacy state migration: implementationRequired was a boolean field in older exports.
    const implementationRequired = rawState.implementationRequired === 'yes';
    // Note: 'guidance' mode has been removed. Legacy 'guidance' states fall back to 'none'
    // since the advisory support toggle in the handover section now serves that purpose.
    return implementationRequired ? 'veeam-ps' : 'none';
}

export function sanitizeToggleState(value, fallback) {
    if (value === 'yes') {
        return 'yes';
    }

    if (value === 'no' || value === 'none') {
        return 'no';
    }

    return fallback;
}

export function normalizeWorkloadEntries(items, idKey, countKey, labels) {
    const uniqueEntries = new Map();

    items.forEach((item) => {
        if (!item || typeof item[idKey] !== 'string' || !Object.hasOwn(labels, item[idKey])) {
            return;
        }

        uniqueEntries.set(item[idKey], {
            [idKey]: item[idKey],
            [countKey]: Math.max(0, parseInt(item[countKey], 10) || 0)
        });
    });

    return Array.from(uniqueEntries.values());
}

export function getSiteById(siteId) {
    return sites.find((site) => site.id === siteId) || null;
}

export function getSiteIndexById(siteId) {
    return sites.findIndex((site) => site.id === siteId);
}

export function hasVbrAcrossAllSites() {
    return sites.some((site) =>
        normalizeToggleMigratedCount(site.existingEnvironment?.vbrServers) > 0 ||
        (Array.isArray(site.veeamServers) && site.veeamServers.some((s) => s.type === 'vbr'))
    );
}

export function hasUnprotectedWorkloadsNeedingVbr() {
    return sites.some((site) =>
        (site.sourceWorkloads || []).some(
            (sw) => (parseInt(sw.totalCount, 10) || 0) > 0 && !isNonVbrWorkload(sw.workloadValue)
        )
    );
}

export function createVeeamServerInstance(type, name = '') {
    const instanceId = `vs-${AppState.nextVeeamServerId++}`;
    const typeDef = VEEAM_SERVER_TYPES[type] || VEEAM_SERVER_TYPES.vbr;
    const defaultName = name || typeDef.label;
    return {
        instanceId,
        type,
        name: defaultName,
        // Sub-components (relevant for VBR)
        proxyServersWindows: 0,
        proxyServersLinux: 0,
        repositoryServers: 0,
        wanAccelerators: 0,
        veeamAgents: 0,
        enterprisePlugins: 0,
        vroServers: 0,
        // Jobs (relevant for VBR)
        backupJobs: 0,
        backupCopyJobs: 0,
        sureBackupJobs: 0,
        replicationJobs: 0,
        cdpPolicies: 0
    };
}

export function hasCloudWorkloads() {
    return sites.some((site) =>
        Array.isArray(site.workloads) &&
        site.workloads.some((w) => w.value && Object.hasOwn(cloudLabels, w.value))
    );
}

export function updateWizardProgress(step) {
    wizardProgressItems.forEach((item) => {
        const itemStep = parseInt(item.dataset.stepIndicator, 10);
        const isActive = itemStep === step;
        const isComplete = itemStep < step;
        const isFuture = itemStep > step;
        const button = item.querySelector('[data-step-target]');

        item.classList.toggle('is-active', isActive);
        item.classList.toggle('is-complete', isComplete);

        const indexEl = item.querySelector('.wizard-progress__index');
        if (indexEl) {
            if (isComplete) {
                indexEl.innerHTML = `<svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><path d="M2.5 6.5L5.5 9.5L10.5 3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
            } else {
                indexEl.textContent = String(itemStep);
            }
        }

        if (isActive) {
            item.setAttribute('aria-current', 'step');
        } else {
            item.removeAttribute('aria-current');
        }

        // Mark future steps as disabled so screen readers announce them correctly.
        if (button) {
            if (isFuture) {
                button.setAttribute('aria-disabled', 'true');
            } else {
                button.removeAttribute('aria-disabled');
            }
        }
    });
}

export function dismissStep3WarningDialog() {
    AppState.pendingStepNavigation = null;

    if (step3WarningDialog instanceof HTMLDialogElement && step3WarningDialog.open) {
        step3WarningDialog.close();
    }
}

export function updateAssumptionsRiskNotice() {
    if (!assumptionsRiskNotice || !assumptionsRiskNoticeText) return;

    const allIds = Object.keys(assumptionToggleLabels);
    const flagged = allIds.filter((id) => {
        const el = document.getElementById(id);
        return el && !el.checked;
    });

    // Apply / remove row-level highlight on each assumption row
    allIds.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const row = el.closest('.property-row');
        if (!row) return;
        row.classList.toggle('property-row--risk-flagged', !el.checked);
    });

    if (flagged.length === 0) {
        assumptionsRiskNotice.hidden = true;
        if (assumptionsRiskNoticeCount) assumptionsRiskNoticeCount.textContent = '';
        assumptionsRiskNoticeText.textContent = '';
    } else {
        const n = flagged.length;
        const labels = flagged.map((id) => assumptionToggleLabels[id]);
        if (assumptionsRiskNoticeCount) {
            assumptionsRiskNoticeCount.textContent = `${n} risk${n === 1 ? '' : 's'} flagged`;
        }
        assumptionsRiskNoticeText.textContent = labels.join('; ');
        assumptionsRiskNotice.hidden = false;
    }
}

export function updateToggleLabel(toggle) {
    const label = toggle.closest('.toggle-control')?.querySelector('.toggle-text');
    if (!label) return;
    label.textContent = toggle.checked ? label.dataset.on : label.dataset.off;
}

export function triggerCloneSheepAnimation(siteId) {
    const currentAnimation = activeCloneSheepAnimations.get(siteId);

    if (currentAnimation?.timeoutId) {
        window.clearTimeout(currentAnimation.timeoutId);
    }

    const animationState = {
        timeoutId: window.setTimeout(() => {
            const latestAnimation = activeCloneSheepAnimations.get(siteId);

            if (latestAnimation !== animationState) {
                return;
            }

            const sheepShell = siteList.querySelector(`[data-site-clone-shell="${siteId}"]`);
            sheepShell?.classList.remove('site-workload-table__clone-shell--sheep-active');
            activeCloneSheepAnimations.delete(siteId);
        }, CLONE_SHEEP_ANIMATION_DURATION_MS)
    };

    activeCloneSheepAnimations.set(siteId, animationState);
}

export function syncCloneSheepAnimations() {
    activeCloneSheepAnimations.forEach((animationState, siteId) => {
        if (!animationState) {
            return;
        }

        const sheepShell = siteList.querySelector(`[data-site-clone-shell="${siteId}"]`);

        if (!sheepShell) {
            return;
        }

        sheepShell.classList.remove('site-workload-table__clone-shell--sheep-active');
        void sheepShell.offsetWidth;
        sheepShell.classList.add('site-workload-table__clone-shell--sheep-active');
    });
}

export function gfGroupLabel(label, count) {
    const tip = GF_CATEGORY_TOOLTIPS[label];
    const iconSvg = window.GF_CATEGORY_ICONS?.[label] ?? '';
    const categoryIcon = iconSvg
        ? `<span class="gf-group-icon" aria-hidden="true">${iconSvg}</span>`
        : '';
    const infoIcon = tip
        ? `<span class="gf-group-info" aria-label="${tip}" tabindex="0" role="note">`
          + `<svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">`
          + `<circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.5"/>`
          + `<path d="M8 7.5v3.5M8 5.5v.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`
          + `</svg>`
          + `<span class="gf-group-info__tip" role="tooltip">${tip}</span>`
          + `</span>`
        : '';
    const countPill = (typeof count === 'number' && count > 0)
        ? `<span class="gf-chip-group-count" aria-label="${count} item${count === 1 ? '' : 's'}">${count}</span>`
        : '';
    return `<span class="gf-chip-group-label">${categoryIcon}${label}${infoIcon}${countPill}</span>`;
}

export function getGfWorkloadGroup(workloadValue) {
    return GF_WORKLOAD_TYPE_MAP[workloadValue] || 'Hypervisors';
}

export function getGfWorkloadLabel(workloadValue) {
    for (const group of workloadOptionGroups) {
        const opt = group.options.find((o) => o.value === workloadValue);
        if (opt) return opt.label;
    }
    return workloadValue;
}

export function getGfWorkloadUnit(workloadValue) {
    for (const group of workloadOptionGroups) {
        const opt = group.options.find((o) => o.value === workloadValue);
        if (opt) return opt.unit || '';
    }
    return '';
}

export function buildJobTypeSelectMarkup(jobId, siteId, selectedValue) {
    const options = jobTypeOptions
        .map((t) => `<option value="${escapeHtml(t.value)}"${t.value === selectedValue ? ' selected' : ''}>${escapeHtml(t.label)}</option>`)
        .join('');
    return `<select class="gf-job-select gf-job-type-select" data-gf-job-type="${jobId}" data-gf-job-site="${siteId}" aria-label="Job type">${options}</select>`;
}

export function buildJobWorkloadSelectMarkup(jobId, siteId, selectedValue, visible) {
    const options = jobWorkloadOptions
        .map((w) => `<option value="${escapeHtml(w.value)}"${w.value === selectedValue ? ' selected' : ''}>${escapeHtml(w.label)}</option>`)
        .join('');
    const hiddenAttr = visible ? '' : ' hidden aria-hidden="true"';
    return `<select class="gf-job-select gf-job-workload-select" data-gf-job-workload="${jobId}" data-gf-job-site="${siteId}" aria-label="Workload type"${hiddenAttr}>${options}</select>`;
}

export function isSubCellNA(subKey, workloadType) {
    if (_MATRIX_ALLOWLIST_SETS[subKey]) {
        return !_MATRIX_ALLOWLIST_SETS[subKey].has(workloadType);
    }
    return _MATRIX_NA_SETS[subKey]?.has(workloadType) ?? false;
}

export function getModifierDefs(group) {
    if (group === 'components') return COMPONENT_MODIFIER_DEFS;
    return (group === 'unmanaged' || group === 'migration') ? UNMANAGED_MODIFIER_DEFS : MANAGED_MODIFIER_DEFS;
}

export function getModifierBlankState(modDef) {
    const s = {};
    modDef.subs.forEach((sub) => { s[sub.key] = false; });
    return s;
}

export function buildEngagementSettingsHtml() {
    const yn = (name, value, label) => `
        <div class="segmented-control" role="radiogroup" aria-label="${label}">
            <label class="segmented-control__option" for="${name}-no">
                <input type="radio" id="${name}-no" name="${name}" value="no"
                    data-engagement-field="${name}"
                    ${value !== 'yes' ? 'checked' : ''}>
                <span class="segmented-control__pill">No</span>
            </label>
            <label class="segmented-control__option" for="${name}-yes">
                <input type="radio" id="${name}-yes" name="${name}" value="yes"
                    data-engagement-field="${name}"
                    ${value === 'yes' ? 'checked' : ''}>
                <span class="segmented-control__pill">Yes</span>
            </label>
        </div>`;

    return `
    <div class="scope-matrix-group scope-matrix-group--settings">
        <div class="scope-matrix-group-hdr">
            <span class="scope-matrix-group-dot" aria-hidden="true"></span>
            <span class="scope-matrix-group-title">Global Engagement Settings</span>
            <span class="scope-matrix-group-desc">Global options that apply across the full engagement</span>
        </div>
        <div class="scope-settings-grid">
            <div class="property-row">
                <span class="property-row__label">VBEM</span>
                ${yn('engagement-vbem', architectureState.vbem, 'VBEM')}
            </div>
            <div class="property-row">
                <span class="property-row__label">Advanced RBAC</span>
                ${yn('engagement-advanced-rbac', architectureState.advancedRbac, 'Advanced RBAC')}
            </div>
        </div>
    </div>`;
}

export function buildMatrixTheadHtml(group, modDefs, expandedSet) {
    const wlLabel = group === 'components' ? 'Component' : 'Workload';
    const ths = [`<th class="scope-matrix-th scope-matrix-th--wl">${wlLabel}</th>`];

    modDefs.forEach((mod) => {
        const isSingleSub = mod.subs.length === 1;
        if (isSingleSub) {
            // Single-sub modifier: plain header — no expand/collapse needed.
            ths.push(`<th class="scope-matrix-th scope-matrix-th--mod scope-matrix-th--plain"
                data-matrix-group="${group}">
                <span class="scope-matrix-mod-label">${mod.label}</span>
            </th>`);
        } else if (!expandedSet.has(mod.key)) {
            // Multi-sub, collapsed — entire th is the expand trigger.
            ths.push(`<th class="scope-matrix-th scope-matrix-th--mod"
                data-matrix-expand="${mod.key}" data-matrix-group="${group}"
                tabindex="0" role="button"
                title="Expand ${mod.label}" aria-label="Expand ${mod.label}">
                <span class="scope-matrix-mod-label">${mod.label}</span>
                <span class="scope-matrix-col-arrow" aria-hidden="true">${MATRIX_EXPAND_SVG}</span>
            </th>`);
        } else {
            // Multi-sub, expanded — one th per sub-column.
            mod.subs.forEach((sub, i) => {
                const isFirst = i === 0;
                const isLast  = i === mod.subs.length - 1;
                const isAffinityLast = sub.affinityGroup && (isLast || mod.subs[i + 1]?.affinityGroup !== sub.affinityGroup);
                const cls = `scope-matrix-th scope-matrix-th--sub${isFirst ? ' is-group-first' : ''}${isLast ? ' is-group-last' : ''}${isAffinityLast && !isLast ? ' is-affinity-last' : ''}`;
                const groupTag = isFirst
                    ? `<span class="scope-matrix-group-tag">
                        <span>${mod.label}</span>
                        <button type="button" class="scope-matrix-col-btn"
                            data-matrix-expand="${mod.key}" data-matrix-group="${group}"
                            title="Collapse ${mod.label}">${MATRIX_COLLAPSE_SVG}</button>
                       </span>`
                    : `<span class="scope-matrix-group-tag"></span>`;
                ths.push(`<th class="${cls}" data-matrix-group="${group}">
                    <div class="scope-matrix-th-inner">
                        ${groupTag}
                        <span class="scope-matrix-sub-name">${sub.label}</span>
                    </div>
                </th>`);
            });
        }
    });

    return `<thead><tr>${ths.join('')}</tr></thead>`;
}

export function buildVeeamServerTypeChipMarkup(typeKey, typeDef, count, siteId, sourceVersion = null, customLabel = '') {
    const label = typeDef.label;
    const displayLabel = customLabel || label;
    const qtyEmpty = count === 0 ? ' is-empty' : '';
    const iconSvg = VEEAM_SERVER_TYPE_ICONS[typeKey] || VEEAM_SERVER_TYPE_ICONS.vbr;
    const versionBadge = sourceVersion
        ? `<span class="gf-chip-version-badge" aria-label="Source version ${escapeHtml(sourceVersion)}">${escapeHtml(sourceVersion)}</span>`
        : '';
    return `<span class="gf-chip gf-chip--comp"
                  data-gf-server-type-chip="${escapeHtml(typeKey)}"
                  data-gf-server-type-site="${siteId}">
        <span class="gf-chip-row-name">
            <span class="gf-chip-grip" title="Drag to reorder">${GF_SVG_GRIP_SM}</span>
            <span class="gf-chip-icon" aria-hidden="true">${iconSvg}</span>
            <span class="gf-chip-label" contenteditable="true" spellcheck="false"
                  data-gf-comp-inline-label="${escapeHtml(typeKey)}"
                  data-gf-comp-site="${siteId}"
                  data-chip-default-label="${escapeHtml(label)}"
                  title="${escapeHtml(label)}"
            >${escapeHtml(displayLabel)}</span><span class="gf-chip-label-pencil" aria-hidden="true">${GF_SVG_PENCIL}</span>
            ${versionBadge}
            <button type="button" class="gf-chip-remove"
                    data-gf-server-type-remove="${escapeHtml(typeKey)}"
                    data-gf-server-type-site="${siteId}"
                    aria-label="Remove ${escapeHtml(label)}">${GF_SVG_CLOSE}</button>
        </span>
        <span class="gf-chip-row-qty">
            <span class="gf-chip-x-symbol" aria-hidden="true">×</span>
            <input class="gf-chip-qty${qtyEmpty}"
                   type="number"
                   min="0"
                   step="1"
                   style="width: ${Math.max(1, count > 0 ? count.toString().length : 1)}ch"
                   value="${count > 0 ? count : ''}"
                   placeholder="0"
                   data-gf-server-type-qty="${escapeHtml(typeKey)}"
                   data-gf-server-type-site="${siteId}"
                   aria-label="Count for ${escapeHtml(label)}">
            <span class="gf-chip-unit">instances</span>
        </span>
    </span>`;
}

export function buildGfCompChipMarkup(item, siteId) {
    const isLocked = item.lockedQtySlot !== undefined;
    const isVb365RepoChip = item.key === 'vb365RepositoryServers';
    const isIndividualVbrChip = item.key === 'vbrServers' && isLocked;
    const count = item.count || 0;
    const versionBadge = (!isVb365RepoChip && item.sourceVersion)
        ? `<span class="gf-chip-version-badge" aria-label="Source version ${escapeHtml(item.sourceVersion)}">${escapeHtml(item.sourceVersion)}</span>`
        : '';
    // VB365 repo chips — per-slot storage type badge (click → opens step drawer to change type)
    const vb365RepoTypeBadgeHtml = isVb365RepoChip ? (() => {
        const typeValue = item.sourceRepoType || 'block';
        const typeLabel = vb365RepoTypeOptions.find((o) => o.value === typeValue)?.label ?? typeValue;
        const shortLabel = typeLabel.replace(/^Object storage\s*[—–-]\s*/i, '').replace(/^Block storage.*$/i, 'Block').replace(/^SMB share$/i, 'SMB');
        const ariaLabel = `Storage: ${typeLabel} — click to edit`;
        return `<button type="button" class="gf-chip-version-badge gf-chip-repo-type-badge"
                    data-gf-vb365-repo-type-badge
                    data-gf-comp-site="${siteId}"
                    data-slot-index="${item.slotIndex ?? ''}"
                    aria-label="${escapeHtml(ariaLabel)}"
                    title="${escapeHtml(ariaLabel)}"
                >${escapeHtml(shortLabel)}</button>`;
    })() : '';
    // Locked-qty chips: VBR uses slot-index-aware decrement; VB365 repos also use slot-index; VRO/VONE use version-aware.
    const removeAttr = isLocked
        ? `data-gf-locked-comp-decrement="${escapeHtml(item.key)}" data-gf-locked-comp-slot-index="${(isVb365RepoChip || isIndividualVbrChip) ? (item.slotIndex ?? '') : ''}" data-gf-locked-comp-version="${escapeHtml(item.sourceVersion ?? '')}" data-gf-locked-comp-platform="${escapeHtml(item.sourcePlatform ?? '')}" data-gf-locked-comp-ha-role="${escapeHtml(item.sourceHaRole ?? '')}" data-gf-locked-comp-ha-pair-id="${escapeHtml(item.sourcePairId ?? '')}" data-gf-comp-site="${siteId}"`
        : `data-gf-comp-chip-remove="${escapeHtml(item.key)}" data-gf-comp-site="${siteId}"`;
    // VB365 repo chips — source storage type label + consumed TB editable inline input (per-slot)
    // Computed here (before qtyRow) so the consumed-TB qty row can reference vb365RepoConsumedTbVal.
    const vb365RepoConsumedTbVal = isVb365RepoChip
        ? ((item.sourceConsumedTb ?? 0) > 0 ? item.sourceConsumedTb : '')
        : null;
    const vb365RepoSourceTypeLabel = isVb365RepoChip
        ? (vb365RepoTypeOptions.find((o) => o.value === (item.sourceRepoType || 'block'))?.label ?? (item.sourceRepoType || 'block'))
        : '';
    // VB365 repo chips: show consumed TB as a qty row (× N TB).
    // Individual VBR chips: static "× 1 instance" label row — matches adjacent chip height, no editable input.
    // Other locked chips (VRO/VONE): editable qty input lets user bulk-add/remove instances.
    const vbrMappedWorkloadCount = isIndividualVbrChip ? (() => {
        const instanceId = `env-vbr-${siteId}-${item.slotIndex ?? 0}`;
        const site = sites.find((s) => s.id === siteId);
        if (!site || !Array.isArray(site.sourceWorkloads)) return 0;
        return site.sourceWorkloads.filter((sw) =>
            Array.isArray(sw.jobs) && sw.jobs.some((j) => j.managedByServerId === instanceId)
        ).length;
    })() : 0;
    const vbrWorkloadBadge = vbrMappedWorkloadCount > 0
        ? `<span class="gf-chip-workload-count-badge">${vbrMappedWorkloadCount} ${vbrMappedWorkloadCount === 1 ? 'workload' : 'workloads'}</span>`
        : '';
    const qtyRow = (isLocked && isIndividualVbrChip)
        ? `<span class="gf-chip-row-qty">
            <span class="gf-chip-x-symbol" aria-hidden="true">×</span>
            <span class="gf-chip-unit">1 instance</span>
            ${vbrWorkloadBadge}
        </span>`
        : (isLocked && isVb365RepoChip)
        ? `<span class="gf-chip-row-qty">
            <span class="gf-chip-x-symbol" aria-hidden="true">×</span>
            <input type="number" class="gf-chip-qty gf-chip-consumed-tb-input${vb365RepoConsumedTbVal === '' ? ' is-empty' : ''}"
                min="0" step="0.1" placeholder="—"
                value="${vb365RepoConsumedTbVal}"
                data-gf-vb365-repo-consumed-tb
                data-gf-comp-site="${siteId}"
                data-slot-index="${item.slotIndex ?? ''}"
                aria-label="Consumed TB for ${escapeHtml(item.displayLabel)}"
                style="width:${Math.max(2, String(vb365RepoConsumedTbVal).length || 2)}ch"
            /><span class="gf-chip-unit">TB</span>${vb365RepoTypeBadgeHtml ? `<span class="gf-chip-qty-badges">${vb365RepoTypeBadgeHtml}</span>` : ''}
        </span>`
        : isLocked
        ? `<span class="gf-chip-row-qty">
            <span class="gf-chip-x-symbol" aria-hidden="true">×</span>
            <input class="gf-chip-qty${count === 0 ? ' is-empty' : ''}"
                   type="number"
                   min="1"
                   step="1"
                   style="width:${Math.max(1, count > 0 ? count.toString().length : 1)}ch"
                   value="${count > 0 ? count : ''}"
                   placeholder="1"
                   data-gf-locked-comp-qty="${escapeHtml(item.key)}"
                   data-gf-locked-comp-version="${escapeHtml(item.sourceVersion ?? '')}"
                   data-gf-locked-comp-platform="${escapeHtml(item.sourcePlatform ?? '')}"
                   data-gf-locked-comp-ha-role="${escapeHtml(item.sourceHaRole ?? '')}"
                   data-gf-locked-comp-ha-pair-id="${escapeHtml(item.sourcePairId ?? '')}"
                   data-gf-comp-site="${siteId}"
                   aria-label="Count for ${escapeHtml(item.displayLabel)}">
            <span class="gf-chip-unit">${count === 1 ? 'instance' : 'instances'}</span>
        </span>`
        : `<span class="gf-chip-row-qty">
            <span class="gf-chip-x-symbol" aria-hidden="true">×</span>
            <input class="gf-chip-qty${count === 0 ? ' is-empty' : ''}"
                   type="number"
                   min="0"
                   step="1"
                   style="width: ${Math.max(1, count > 0 ? count.toString().length : 1)}ch"
                   value="${count > 0 ? count : ''}"
                   placeholder="0"
                   data-gf-comp-chip-qty="${escapeHtml(item.key)}"
                   data-gf-comp-site="${siteId}"
                   aria-label="Count for ${escapeHtml(item.displayLabel)}">
            <span class="gf-chip-unit">instances</span>
        </span>`;
    // VB365 repo chips — source type is shown as a clickable badge in the chip header, so no meta row needed.
    const vb365RepoConsumedTbHtml = '';
    // HA pair badge — shown on VSA HA chips; amber cross-site variant when peer is on a different site
    const haRole   = item.sourceHaRole  || null;
    const haPairId = item.sourcePairId  || null;
    const haBadge  = (haRole && haPairId)
        ? (() => {
            const isCross = sites.some((s) => {
                if (s.id === siteId) return false;
                const pids = s.existingEnvironmentVbrHaPairIds || {};
                return Object.values(pids).some((pid) => pid === haPairId);
            });
            const roleLabel = haRole === 'primary' ? 'primary' : 'secondary';
            if (isCross) {
                return `<span class="gf-chip-ha-badge gf-chip-ha-badge--cross" title="HA pair — nodes are on different sites">HA ${roleLabel} · cross-site</span>`;
            }
            return `<span class="gf-chip-ha-badge" title="HA pair">HA ${roleLabel}</span>`;
        })()
        : '';
    const haPairAttr  = haPairId ? ` data-gf-ha-pair-id="${escapeHtml(haPairId)}"` : '';
    const haRoleAttr  = haRole   ? ` data-gf-ha-role="${escapeHtml(haRole)}"`       : '';
    const vb365RepoSlotAttr = isVb365RepoChip && item.slotIndex !== undefined ? ` data-gf-comp-slot-index="${item.slotIndex}"` : '';
    return `<span class="gf-chip gf-chip--comp${isLocked ? ' gf-chip--qty-locked' : ''}" data-gf-comp-chip="${escapeHtml(item.key)}" data-gf-comp-site="${siteId}"${isLocked && item.sourceVersion ? ` data-gf-comp-version="${escapeHtml(item.sourceVersion)}"` : ''}${vb365RepoSlotAttr}${haPairAttr}${haRoleAttr}>
        <span class="gf-chip-row-name">
            <span class="gf-chip-grip" title="Drag to reorder">${GF_SVG_GRIP_SM}</span>
            <span class="gf-chip-icon" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg></span>
            <span class="gf-chip-label" contenteditable="true" spellcheck="false"
                  data-gf-comp-inline-label="${escapeHtml(item.key)}"
                  data-gf-comp-site="${siteId}"
                  data-chip-default-label="${escapeHtml(item.displayLabel)}"
                  title="${escapeHtml(item.displayLabel)}"
            >${escapeHtml(item.customLabel || item.displayLabel)}</span><span class="gf-chip-label-pencil" aria-hidden="true">${GF_SVG_PENCIL}</span>
            ${versionBadge}${haBadge}
            <button type="button" class="gf-chip-remove" ${removeAttr} aria-label="Remove ${escapeHtml(item.displayLabel)}">${GF_SVG_CLOSE}</button>
        </span>
        ${qtyRow}
        ${vb365RepoConsumedTbHtml}
    </span>`;
}

export function buildGfAddSiteCardMarkup() {
    return `<button type="button" class="gf-add-site-card" id="gf-add-site-btn">
        ${GF_SVG_PLUS}Add site
    </button>`;
}

export function destroyGreenfieldSortables() {
    if (gfCanvasSortable) {
        try { gfCanvasSortable.destroy(); } catch (_) {}
        gfCanvasSortable = null;
    }
    gfWlSortables.forEach((s) => { try { s.destroy(); } catch (_) {} });
    gfWlSortables.length = 0;
}

export function getReliableDropIndex(evt) {
    if (evt.item && evt.to) {
        const chips = Array.from(evt.to.querySelectorAll(':scope > .gf-chip'));
        const pos = chips.indexOf(evt.item);
        if (pos !== -1) return pos;
    }
    return evt.newDraggableIndex ?? evt.newIndex ?? 0;
}

export function setAddSiteDropHover(isOver) {
    document.getElementById('gf-add-site-btn')?.classList.toggle('gf-add-site-card--drop-hover', isOver);
}

export function buildSiteListPlaceholderMarkup() {
    return `
        <div class="site-list__placeholder" role="status" aria-live="polite" aria-atomic="true">
            <p class="site-list__placeholder-copy">${escapeHtml(engagementTypePlaceholderMessage)}</p>
        </div>
    `;
}

export function buildSiteListContentMarkup(markup) {
    return `<div class="site-list__content">${markup}</div>`;
}

export function applyStaggeredReveal(container = siteList) {
    const staggerGroups = Array.from(container.querySelectorAll('[data-stagger-group]'));

    staggerGroups.forEach((group, groupIndex) => {
        const delay = `${(groupIndex * 0.1).toFixed(1)}s`;
        const targets = group.matches('tbody')
            ? Array.from(group.querySelectorAll('tr')).filter((row) => !row.classList.contains('site-workload-table__site-gap'))
            : [group];

        targets.forEach((target) => {
            target.classList.add('staggered-item');
            target.style.animationDelay = delay;
        });
    });
}

export function handleCategoryAdjustInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.categoryAdjust) {
        return;
    }

    const categoryId = input.dataset.categoryAdjust;
    const recommended = parseInt(input.dataset.recommended, 10) || 0;
    const rawValue = parseInt(input.value, 10);
    const newValue = isNaN(rawValue) ? recommended : Math.max(0, rawValue);

    // Persist the adjustment (or clear it if back to recommended)
    if (newValue === recommended) {
        categoryHourAdjustments.delete(categoryId);
    } else {
        categoryHourAdjustments.set(categoryId, newValue);
    }

    // Update this row's delta cell
    const delta = newValue - recommended;
    const deltaEl = output.querySelector(`[data-delta-for="${categoryId}"]`);
    if (deltaEl) {
        deltaEl.textContent = delta === 0 ? '—' : (delta > 0 ? `+${delta} hrs` : `${delta} hrs`);
        deltaEl.className = 'results-effort-table__delta ' + (
            delta > 0 ? 'results-effort-table__delta--increase' :
            delta < 0 ? 'results-effort-table__delta--decrease' :
            'results-effort-table__delta--neutral'
        );
    }

    // Recompute overall totals from all inputs currently in the DOM
    const allInputs = output.querySelectorAll('[data-category-adjust]');
    let totalRec = 0;
    let totalAdj = 0;
    allInputs.forEach((inp) => {
        const rec = parseInt(inp.dataset.recommended, 10) || 0;
        const adj = categoryHourAdjustments.has(inp.dataset.categoryAdjust)
            ? categoryHourAdjustments.get(inp.dataset.categoryAdjust)
            : rec;
        totalRec += rec;
        totalAdj += adj;
    });
    const totalDelta = totalAdj - totalRec;

    // Update tfoot total row
    const adjTotalEl = document.getElementById('effort-table-adj-total');
    const totalDeltaEl = document.getElementById('effort-table-total-delta');
    if (adjTotalEl) {
        adjTotalEl.textContent = String(totalAdj);
    }
    if (totalDeltaEl) {
        totalDeltaEl.textContent = totalDelta === 0 ? '—' : (totalDelta > 0 ? `+${totalDelta} hrs` : `${totalDelta} hrs`);
        totalDeltaEl.className = 'results-effort-table__delta ' + (
            totalDelta > 0 ? 'results-effort-table__delta--increase' :
            totalDelta < 0 ? 'results-effort-table__delta--decrease' :
            'results-effort-table__delta--neutral'
        );
    }

    // Update summary metric card
    const summaryValueEl = document.getElementById('summary-effort-value');
    const summaryMetaEl = document.getElementById('summary-effort-meta');
    const hasAdj = totalDelta !== 0;
    const displayHrs = hasAdj ? totalAdj : totalRec;
    const displayDays = Math.ceil(displayHrs / 8);

    if (summaryValueEl) {
        summaryValueEl.textContent = `${displayHrs} hours`;
    }
    if (summaryMetaEl) {
        const tagClass = totalDelta > 0
            ? 'results-metric-card__adj-tag--increase'
            : 'results-metric-card__adj-tag--decrease';
        const tagSign = totalDelta > 0 ? '+' : '';
        const tagHtml = hasAdj
            ? ` &middot; <span class="results-metric-card__adj-tag ${tagClass}">${tagSign}${totalDelta} hrs adjusted</span>`
            : '';
        summaryMetaEl.innerHTML = `${displayDays} delivery days${tagHtml}`;
    }
}

export function handlePhaseStepperClick(event) {
    const btn = event.target.closest('.phase-stepper-btn');
    if (!btn) return;

    const phaseId     = btn.dataset.phaseId;
    const step        = parseInt(btn.dataset.phaseStep, 10);
    const recommended = parseInt(btn.dataset.phaseRecommended, 10) || 0;

    const current = phaseHourAdjustments.has(phaseId)
        ? phaseHourAdjustments.get(phaseId)
        : recommended;
    const newHrs  = Math.max(0, current + step);

    if (newHrs === recommended) {
        phaseHourAdjustments.delete(phaseId);
    } else {
        phaseHourAdjustments.set(phaseId, newHrs);
    }

    // Update this card's displayed hours and days
    const hrsEl  = document.querySelector(`[data-phase-hrs="${phaseId}"]`);
    const daysEl = document.querySelector(`[data-phase-days="${phaseId}"]`);
    if (hrsEl)  hrsEl.textContent = String(newHrs);
    if (daysEl) daysEl.innerHTML  = `${Math.ceil(newHrs / 8)} days`;

    // Recompute totals from all phase cards currently in the DOM
    let totalAdj = 0;
    let totalRec = 0;
    document.querySelectorAll('[data-phase-hrs]').forEach((el) => {
        totalAdj += parseInt(el.textContent, 10) || 0;
    });
    document.querySelectorAll('.phase-stepper-btn[data-phase-step="-4"]').forEach((b) => {
        totalRec += parseInt(b.dataset.phaseRecommended, 10) || 0;
    });
    const delta      = totalAdj - totalRec;
    const displayDays = Math.ceil(totalAdj / 8);

    // Update the summary metric card
    const summaryValueEl = document.getElementById('summary-effort-value');
    const summaryMetaEl  = document.getElementById('summary-effort-meta');
    if (summaryValueEl) summaryValueEl.textContent = `${totalAdj} hours`;
    if (summaryMetaEl) {
        const tagClass = delta > 0 ? 'results-metric-card__adj-tag--increase' : 'results-metric-card__adj-tag--decrease';
        const tagSign  = delta > 0 ? '+' : '';
        const tagHtml  = delta !== 0
            ? ` &middot; <span class="results-metric-card__adj-tag ${tagClass}">${tagSign}${delta} hrs adjusted</span>`
            : '';
        summaryMetaEl.innerHTML = `${displayDays} delivery days${tagHtml}`;
    }
}

export function destroyStep2Sortables() {
    if (s2SortableTray) { try { s2SortableTray.destroy(); } catch (_) {} s2SortableTray = null; }
    s2SortableLanes.forEach((s) => { try { s.destroy(); } catch (_) {} });
    s2SortableLanes.length = 0;
}

export function getS2ReuseableVbrs() {
    const usedInstanceIds = new Set(
        step2VbrLanes.filter((l) => l.type === 'existing').map((l) => l.sourceInstanceId)
    );
    const result = [];
    sites.forEach((site) => {
        const siteName  = site.name || `Site ${site.id}`;
        const namedVbrs = (site.veeamServers || []).filter((sv) => sv.type === 'vbr');
        if (namedVbrs.length > 0) {
            // Named instances take priority — use them directly
            namedVbrs
                .filter((sv) => !usedInstanceIds.has(sv.instanceId))
                .forEach((sv) => result.push({ instanceId: sv.instanceId, name: sv.name || 'VBR Server', siteId: site.id, siteName }));
        } else {
            // Fall back to count-based from existingEnvironment
            const envCount  = normalizeEnvironmentCount((site.existingEnvironment || {}).vbrServers);
            const haRoles   = site.existingEnvironmentVbrHaRoles   || {};
            const platforms = site.existingEnvironmentVbrPlatforms || {};
            // Count non-secondary slots to produce correct ordinal labels
            let nonSecondaryCount = 0;
            for (let i = 0; i < envCount; i++) {
                if ((haRoles[i] || null) !== 'secondary') nonSecondaryCount++;
            }
            let ordinal = 0;
            for (let i = 0; i < envCount; i++) {
                // HA secondary nodes are not independent VBRs — the primary represents the whole cluster
                if ((haRoles[i] || null) === 'secondary') continue;
                const syntheticId = `env-vbr-${site.id}-${i}`;
                if (!usedInstanceIds.has(syntheticId)) {
                    const haRole   = haRoles[i]   || null;
                    const platform = platforms[i] || null;
                    const isVsaHa  = haRole === 'primary' && platform === 'linux';
                    ordinal++;
                    result.push({
                        instanceId: syntheticId,
                        name:       nonSecondaryCount === 1
                                        ? (isVsaHa ? 'VBR VSA HA' : 'VBR Server')
                                        : (isVsaHa ? `VBR VSA HA ${ordinal}` : `VBR Server ${ordinal}`),
                        siteId:     site.id,
                        siteName,
                    });
                }
            }
        }
    });
    return result;
}

/**
 * Normalise a site.sourceWorkloads entry to the shape the Step 2 canvas expects.
 * totalCount aggregates all sub-jobs. managedByServerId is taken from the first
 * job that has one (backup-type jobs are preferred, then any).
 */
export function normalizeSourceWorkloadForS2(sw, site) {
    const jobs = sw.jobs || [];
    let managedByServerId =
        (jobs.find((j) => j.type === 'backup' && j.managedByServerId) || jobs.find((j) => j.managedByServerId))
            ?.managedByServerId || null;
    // Translate legacy 'ee-vbr-{slot}' IDs (written before the convention was fixed)
    // to 'env-vbr-{siteId}-{slot}' so they match Step 2 lane sourceInstanceIds.
    if (managedByServerId && site) {
        const m = managedByServerId.match(/^ee-vbr-(\d+)$/);
        if (m) managedByServerId = `env-vbr-${site.id}-${m[1]}`;
    }
    return {
        value:             sw.workloadValue || 'vmware-vsphere',
        rowId:             S2_UNPROTECTED_ID_OFFSET + sw.id,
        count:             sw.totalCount || 0,
        customLabel:       sw.label || null,
        siteName:          site ? (site.name || `Site ${site.id}`) : null,
        managedByServerId,
    };
}

export function normalizeMigrationScopeForS2(scope) {
    return {
        value:       scope.targetPlatform || 'hyperv',
        rowId:       S2_MIGRATION_ID_OFFSET + scope.id,
        count:       scope.targetVmCount || 0,
        customLabel: null,
        siteName:    null,
    };
}

export function getS2MigrationWorkloads() {
    return hypervisorMigrationScopes
        .filter((s) => s.includeInScope && s.targetPlatform)
        .map(normalizeMigrationScopeForS2);
}

/**
 * Returns true for workloads that are categorically managed by a non-VBR
 * product and therefore never appear in the VBR mapping lanes:
 *   - Microsoft 365      → always VB365 (AUTO)
 *   - AWS / Azure / GCP  → always VBfCloud appliance
 *   - Kubernetes         → always Kasten K10 (AUTO, one instance per cluster chip)
 *
 * Physical (agents) are intentionally NOT excluded — they can run in
 * VBR-managed mode and should remain available for VBR lane assignment.
 */
export function isNonVbrWorkload(value) {
    return isVb365ManagedWorkload(value)
        || isVbfCloudManagedWorkload(value)
        || value === kastenK10WorkloadId;
}

/** Returns all Kubernetes source workload chips across all sites,
 *  normalised for the Step 2 canvas. Each chip maps to one Kasten AUTO card. */
export function getS2KastenWorkloads() {
    return sites.flatMap((site) =>
        (site.sourceWorkloads || [])
            .map((sw) => normalizeSourceWorkloadForS2(sw, site))
            .filter((sw) => sw.value === kastenK10WorkloadId)
    );
}

export function buildS2NewVbrForm() {
    return `<div class="s2-picker-panel" data-s2-picker="new">
        <p class="s2-picker-label">Which site will this VBR be deployed to?</p>
        <div class="s2-site-picker-options">
            ${sites.map((site) => `
                <button type="button" class="s2-site-picker-btn" data-s2-new-vbr-site="${site.id}">
                    ${escapeHtml(site.name || `Site ${site.id}`)}
                </button>`).join('')}
            <button type="button" class="s2-site-picker-btn s2-site-picker-btn--add-site" data-s2-new-site-vbr>
                + New site
            </button>
            <button type="button" class="s2-picker-cancel-btn" data-s2-picker-cancel>Cancel</button>
        </div>
    </div>`;
}

// ── Job-to-Server Linking Helpers ─────────────────────────────────────────────

/**
 * Cloud provider string ('aws'|'azure'|'gcp') → Veeam server type key.
 * Used to match cloud jobs to the correct VBfCloud appliance type.
 */
const CLOUD_PROVIDER_TO_SERVER_TYPE = { aws: 'vbfaws', azure: 'vbfaz', gcp: 'vbfgc' };

/**
 * Returns the set of veeamServer type keys that are compatible with a given job.
 *   - M365 workloads         → ['vb365']
 *   - Kubernetes (Kasten K10) → ['kasten']
 *   - AWS/Azure/GCP workloads → one of ['vbfaws'] | ['vbfaz'] | ['vbfgc']
 *   - All other workloads     → ['vbr']
 */
export function getCompatibleServerTypesForJob(job) {
    const wt = job.workloadType;
    if (isVb365ManagedWorkload(wt))   return ['vb365'];
    if (wt === kastenK10WorkloadId)    return ['kasten'];
    const cloudProvider = getVbfCloudProvider(wt);
    if (cloudProvider)                 return [CLOUD_PROVIDER_TO_SERVER_TYPE[cloudProvider]];
    return ['vbr'];
}

/**
 * Returns all { server, site } pairs across all sites whose type is compatible
 * with the given job. Results are ordered by site then by server name.
 */
/**
 * VBR servers are added to a site in two ways:
 *   1. Named instance  → site.veeamServers entry  (instanceId like 'vs-1')
 *   2. Legacy count chip → site.existingEnvironment.vbrServers  (a number)
 *
 * This helper returns an array of synthetic { server, site } objects for each
 * legacy VBR slot on a site, using the same 'env-vbr-{siteId}-{i}' instanceId
 * convention already used by getS2ReuseableVbrs() — so IDs stay consistent.
 *
 * Returns [] when:
 *   - the site has no legacy VBR count, or
 *   - the site already has at least one named VBR instance (prefer real entries).
 */
function getSyntheticLegacyVbrsForSite(site) {
    const namedVbrs = (site.veeamServers || []).filter((s) => s.type === 'vbr');
    if (namedVbrs.length > 0) return [];          // named instances take precedence
    const count     = normalizeEnvironmentCount((site.existingEnvironment || {}).vbrServers);
    if (count === 0) return [];
    const haRoles   = site.existingEnvironmentVbrHaRoles   || {};
    const platforms = site.existingEnvironmentVbrPlatforms || {};
    const nonSecondaryCount = Array.from({ length: count }, (_, i) => haRoles[i] || null)
        .filter((r) => r !== 'secondary').length;
    const customLabel = (site.existingEnvironmentCustomLabels || {})['vbrServers'] || '';
    const entries = [];
    let ordinal = 0;
    for (let i = 0; i < count; i++) {
        if ((haRoles[i] || null) === 'secondary') continue; // HA secondary is not independent
        ordinal++;
        const haRole   = haRoles[i]   || null;
        const platform = platforms[i] || null;
        const isVsaHa  = haRole === 'primary' && platform === 'linux';
        // Prefer the chip's custom label (set when the user renames the chip); fall back to
        // the default name. For multi-server cases, append an ordinal so each picker entry
        // stays distinct regardless of whether a custom label is in use.
        const defaultLabel = isVsaHa ? 'VBR VSA HA' : 'VBR Server';
        const baseLabel    = customLabel || defaultLabel;
        const label        = nonSecondaryCount === 1 ? baseLabel : `${baseLabel} ${ordinal}`;
        entries.push({
            server: {
                instanceId:        `env-vbr-${site.id}-${i}`,
                type:              'vbr',
                name:              label,
                isLegacyComponent: true
            },
            site
        });
    }
    return entries;
}

export function getCompatibleServersForJob(job) {
    const compatibleTypes = new Set(getCompatibleServerTypesForJob(job));
    const results = [];
    for (const site of sites) {
        // Named server instances (all types: vbr, vb365, kasten, vbfaws, vbfaz, vbfgc)
        for (const server of (site.veeamServers || [])) {
            if (compatibleTypes.has(server.type)) {
                results.push({ server, site });
            }
        }
        // Legacy VBR component chips — only needed for VBR-compatible jobs
        if (compatibleTypes.has('vbr')) {
            results.push(...getSyntheticLegacyVbrsForSite(site));
        }
    }
    return results;
}

/**
 * Returns the { server, site } pair for the server linked to this job via
 * job.managedByInstanceId, or null if the job is unlinked or the server
 * no longer exists.
 * Also resolves synthetic legacy VBR instanceIds (env-vbr-{siteId}-{i}).
 */
export function getLinkedServerForJob(job) {
    if (!job.managedByInstanceId) return null;

    // Check named server instances first
    for (const site of sites) {
        const server = (site.veeamServers || []).find(
            (s) => s.instanceId === job.managedByInstanceId
        );
        if (server) return { server, site };
    }

    // Resolve synthetic legacy VBR entries (env-vbr-{siteId}-{i})
    const legacyMatch = job.managedByInstanceId.match(/^env-vbr-(\d+)-(\d+)$/);
    if (legacyMatch) {
        const siteId = parseInt(legacyMatch[1], 10);
        const site   = sites.find((s) => s.id === siteId);
        if (site) {
            const entry = getSyntheticLegacyVbrsForSite(site)
                .find((e) => e.server.instanceId === job.managedByInstanceId);
            if (entry) return entry;
        }
    }

    return null;
}

// Syncs the visible text labels on implementation-mode radio buttons to the
// implementationModeLabels map. Moved here from workload-defs.js to keep that
// module free of DOM dependencies.
export function syncImplementationModeLabels() {
    document.querySelectorAll('.implementation-option').forEach((label) => {
        const input = label.querySelector('input[name="implementation-mode"]');
        const titleEl = label.querySelector('.implementation-option__title');
        if (input && titleEl && Object.hasOwn(implementationModeLabels, input.value)) {
            titleEl.textContent = implementationModeLabels[input.value];
        }
    });
}

// ── Workload normalisation ────────────────────────────────────────────────────
// Moved from script.js so state-io.js can import them without pulling in the
// full script.js dependency chain.

/** Returns true if the site name looks auto-generated rather than user-typed. */
export function isGeneratedSiteName(name, index = null) {
    const normalizedName = normalizeSiteName(name);
    if (!normalizedName) {
        return false;
    }

    if (index !== null && (normalizedName === getDefaultSiteName(index) || normalizedName === `Site ${index + 1}`)) {
        return true;
    }

    return normalizedName === 'Primary Site'
        || normalizedName === 'Secondary Site'
        || /^Tertiary Site \(\d+\)$/.test(normalizedName)
        || /^Site \d+$/.test(normalizedName);
}

/**
 * Builds a single normalised workload entry object.
 * Pure data factory — no DOM access, no side effects.
 */
export function buildWorkloadEntry(workloadValue, count = 0, protectedTb = null, customLabel = '', rowId = null, extraState = {}) {
    const nextWorkloadValue = normalizeWorkloadValue(workloadValue);
    const normalizedWorkloadValue = hasSelectedWorkloadValue(nextWorkloadValue) ? nextWorkloadValue : '';
    const normalizedCustomLabel = normalizeWorkloadCustomLabel(customLabel);
    const normalizedRowId = createWorkloadRowId(rowId);

    if (!normalizedWorkloadValue) {
        return {
            rowId: normalizedRowId,
            value: '',
            count: 0
        };
    }

    if (isProtectedTbWorkload(normalizedWorkloadValue)) {
        const normalizedProtectedTb = protectedTb === null || protectedTb === undefined || protectedTb === ''
            ? Math.max(0, (parseInt(count, 10) || 0) / 2)
            : normalizeProtectedTb(protectedTb);

        return {
            rowId: normalizedRowId,
            value: normalizedWorkloadValue,
            count: calculateUnstructuredDataInstances(normalizedProtectedTb),
            protectedTb: normalizedProtectedTb,
            ...(isRepoMigrationWorkload(normalizedWorkloadValue)
                ? sanitizeRepoMigrationRowState(extraState)
                : {}),
            ...(normalizedCustomLabel ? { customLabel: normalizedCustomLabel } : {})
        };
    }

    return {
        rowId: normalizedRowId,
        value: normalizedWorkloadValue,
        count: Math.max(0, parseInt(count, 10) || 0),
        ...(normalizedCustomLabel ? { customLabel: normalizedCustomLabel } : {})
    };
}

/**
 * Normalises an array of raw workload items into a deduplicated array of
 * workload entry objects. Merges duplicates; sums protectedTb for unstructured
 * data workloads. Pending (no value) entries are preserved at the end.
 */
export function normalizeSiteWorkloads(items) {
    const uniqueEntries = new Map();
    const pendingEntries = [];

    items.forEach((item) => {
        if (!item || typeof item !== 'object') {
            return;
        }

        const rowId = Number.isInteger(item.rowId) && item.rowId > 0
            ? item.rowId
            : Number.isInteger(item.id) && item.id > 0
                ? item.id
                : null;

        const workloadValue = typeof item.value === 'string'
            ? item.value
            : typeof item.workload === 'string'
                ? item.workload
                : typeof item.hypervisor === 'string'
                    ? item.hypervisor
                    : typeof item.plugin === 'string'
                        ? item.plugin
                        : typeof item.agent === 'string'
                            ? item.agent
                            : '';

        if (workloadValue === '') {
            pendingEntries.push(buildWorkloadEntry('', 0, null, '', rowId));
            return;
        }

        if (!hasSelectedWorkloadValue(workloadValue)) {
            return;
        }

        const rawCount = 'quantity' in item ? item.quantity : item.count;
        const rawProtectedTb = 'protectedTb' in item
            ? item.protectedTb
            : 'tb' in item
                ? item.tb
                : null;
        const rawCustomLabel = 'customLabel' in item ? item.customLabel : '';
        // Pass item as extraState so vault migration rows preserve vdcVaultSourceStorageType,
        // vdcVaultRepositoryType, vdcVaultTargetVault, and isRepositoryComponent on round-trip.
        const normalizedEntry = buildWorkloadEntry(workloadValue, rawCount, rawProtectedTb, rawCustomLabel, rowId, item);

        // Vault migration rows must NOT be deduplicated — each row is independently configured
        // (different source storage type / repository type per row). Key by rowId so multiple
        // rows with the same workload value are each preserved as distinct entries.
        if (isRepoMigrationWorkload(workloadValue)) {
            const vaultKey = rowId != null ? `${workloadValue}:${rowId}` : `${workloadValue}:idx${uniqueEntries.size}`;
            uniqueEntries.set(vaultKey, normalizedEntry);
            return;
        }

        const existingEntry = uniqueEntries.get(workloadValue);
        const mergedCustomLabel = normalizeWorkloadCustomLabel(existingEntry?.customLabel || normalizedEntry.customLabel || '');

        if (existingEntry && isUnstructuredDataWorkload(workloadValue)) {
            uniqueEntries.set(
                workloadValue,
                buildWorkloadEntry(
                    workloadValue,
                    0,
                    (existingEntry.protectedTb || 0) + (normalizedEntry.protectedTb || 0),
                    mergedCustomLabel,
                    existingEntry.rowId
                )
            );
            return;
        }

        if (existingEntry) {
            uniqueEntries.set(
                workloadValue,
                buildWorkloadEntry(workloadValue, existingEntry.count + normalizedEntry.count, null, mergedCustomLabel, existingEntry.rowId)
            );
            return;
        }

        uniqueEntries.set(workloadValue, normalizedEntry);
    });

    return [...Array.from(uniqueEntries.values()), ...pendingEntries];
}

